import React from 'react';
import styled from 'styled-components';

// O documento editável chega como texto, com cada tabela do DOCX em markdown
// (pipes) — ver _parse_docx/_document_blocks na API. A visualização desenha
// essas tabelas como tabelas; o resto segue uma linha por parágrafo.

export type DocumentBlock =
  | { type: 'p'; text: string }
  | { type: 'table'; header: string[] | null; rows: string[][] };

const SEPARATOR = /^\|(\s*:?-{3,}:?\s*\|)+$/;

const isPipeRow = (line: string) => {
  const trimmed = line.trim();
  return trimmed.length >= 2 && trimmed.startsWith('|') && trimmed.endsWith('|');
};

const splitRow = (line: string) => line.trim().slice(1, -1).split('|').map((cell) => cell.trim());

// Mesma regra da API: duas ou mais linhas seguidas entre pipes são tabela, e
// a linha "| --- |" logo após a primeira faz dela o cabeçalho.
export const documentBlocks = (text: string): DocumentBlock[] => {
  const lines = text.split('\n');
  const blocks: DocumentBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    let j = i;
    while (j < lines.length && isPipeRow(lines[j])) j += 1;
    if (j - i < 2) {
      blocks.push({ type: 'p', text: lines[i] });
      i += 1;
      continue;
    }
    let rows = lines.slice(i, j).map(splitRow);
    let header: string[] | null = null;
    if (SEPARATOR.test(lines[i + 1].trim())) {
      header = rows[0];
      rows = rows.slice(2);
    }
    blocks.push({ type: 'table', header, rows });
    i = j;
  }
  return blocks;
};

const Page = styled.div`
  font-family: Georgia, serif;
  line-height: 1.8;
`;

const Paragraph = styled.div`
  white-space: pre-wrap;
  min-height: 1.8em;
`;

const TableScroll = styled.div`
  overflow-x: auto;
  margin: 8px 0 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  line-height: 1.5;
  font-size: 0.95em;

  th, td {
    border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
    padding: 6px 10px;
    text-align: left;
    vertical-align: top;
  }

  th {
    font-weight: 600;
    background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)'};
  }
`;

const DocumentTextPreview: React.FC<{ text: string }> = ({ text }) => (
  <Page>
    {documentBlocks(text).map((block, index) => (block.type === 'p' ? (
      <Paragraph key={index}>{block.text}</Paragraph>
    ) : (
      <TableScroll key={index}>
        <Table>
          {block.header && (
            <thead>
              <tr>{block.header.map((cell, c) => <th key={c}>{cell}</th>)}</tr>
            </thead>
          )}
          <tbody>
            {block.rows.map((row, r) => (
              <tr key={r}>{row.map((cell, c) => <td key={c}>{cell}</td>)}</tr>
            ))}
          </tbody>
        </Table>
      </TableScroll>
    )))}
  </Page>
);

export default DocumentTextPreview;
